import { useEffect, useState } from 'react';
import { BTN_PRIMARY } from '../buttonStyles';
import ButtonSpinner from '../ButtonSpinner';
import { Skeleton, SkeletonGroup } from '../Skeleton';
import { PANE_CARD, TEXT_ACTION } from './surfaces';
import { actionCardModel, matchLength, stackCards, type LiveChange, type PrimaryAct, type SecondaryAct } from '../../utils/actionCard';
import { useMatchVideoUrl } from '../../hooks/useMatchVideoUrl';
import { flipCard, flipOf } from '../../utils/actionCardStore';
import { useActionCardStore } from '../../hooks/useActionCardStore';

const SEEN_KEY = 'db:action-results-seen';
function readSeen(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) ?? '[]') as string[]); } catch { return new Set(); }
}
function writeSeen(ids: string[]) {
  try {
    const all = [...new Set([...readSeen(), ...ids])].slice(-200);
    localStorage.setItem(SEEN_KEY, JSON.stringify(all));
  } catch { /* shown again next visit */ }
}

/** A time to pick (components.md 6's time capsule): 44 high, Subhead 15 medium; on is the Tint wash and ring. */
const ROW_CHOICE =
  'ios-press flex h-11 w-full cursor-pointer items-center justify-center rounded-[100px] px-4 text-[15px] leading-[20px] ' +
  'font-medium tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40';
const ROW_OFF = 'border border-[rgba(0,0,0,0.08)] bg-white text-db-text-1';
const ROW_ON = 'bg-[rgba(26,107,138,0.12)] text-biscayne ring-[1.5px] ring-inset ring-biscayne';
const TITLE = 'text-[17px] leading-[22px] font-semibold tracking-[-0.43px] text-db-text-1';
const BODY = 'text-[15px] leading-[20px] text-db-text-1 tabular-nums';
const NOTE = 'text-[15px] leading-[20px] text-db-text-2';

export type ActionAnswer = { act: PrimaryAct | SecondaryAct; value?: string };

/**
 * The Action card (decision 165 as amended, specs/changes.md 2): every change between a player and a coach, at
 * the top of the page it belongs to and in the thread.
 * - Title, then the old thing struck through and the new in bold, or the times as rows (the count rule).
 * - One primary at most, the blue capsule; one secondary text action.
 * - The answers on speed.md 2's list (confirm, Looks Good, Withdraw) flip the card at once and run behind; a
 *   refusal puts it back. Join and Open Lesson show the primary's spinner in place until they resolve.
 * `onAnswer` returns the call's promise; the card owns its own loading and result.
 */
export function ActionCard({ change, me, tz, onAnswer, onFlip, className = '' }: {
  change: LiveChange;
  me: 'coach' | 'player';
  tz: string;
  onAnswer: (a: ActionAnswer, change: LiveChange) => Promise<unknown> | void;
  /** The card's own result, or null when a refusal put it back: the stack keeps it once the change leaves. */
  onFlip?: (next: LiveChange | null) => void;
  className?: string;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  // The optimistic result, shared by every copy of this card (utils/actionCardStore): the list's and the pane's.
  useActionCardStore();
  const local = flipOf(change.id) ?? null;
  const setLocal = (next: LiveChange | null) => flipCard(change.id, next);
  const [working, setWorking] = useState(false);
  const shown = local ?? change;
  const m = actionCardModel(shown, me, tz, picked);

  const flip = (next: LiveChange, a: ActionAnswer) => {
    setLocal(next);
    onFlip?.(next);
    void Promise.resolve(onAnswer(a, change)).catch(() => { setLocal(null); onFlip?.(null); });
  };
  const press = (act: PrimaryAct | SecondaryAct) => {
    if (act === 'confirm') {
      const value = change.to.length === 1 ? change.to[0] : picked;
      if (!value) return;
      flip({ ...change, status: 'answered', answer: value }, { act, value });
    } else if (act === 'looks_good') {
      flip({ ...change, status: 'cleared' }, { act });
    } else if (act === 'withdraw') {
      flip({ ...change, status: 'withdrawn' }, { act });
    } else if (act === 'open' && (change.kind === 'match' || change.kind === 'counted')) {
      // Opening the lesson is the upload's (or the count's) answer here (the walk of 4 Nov, BOOKING 5): the card
      // goes from every page at once and on later visits; the thread keeps its line.
      setLocal({ ...change, status: 'cleared' });
      writeSeen([change.id]);
      void onAnswer({ act }, change);
    } else if (act === 'join' || act === 'open') {
      const p = onAnswer({ act }, change);
      if (p) {
        setWorking(true);
        void p.finally(() => setWorking(false));
      }
    } else {
      void onAnswer({ act }, change);
    }
  };

  return (
    <section className={`${PANE_CARD} ${className}`} data-action-card={change.id} data-action-kind={change.kind}
      data-action-done={m.done ? '' : undefined} aria-label={m.title}>
      <div className="flex items-start gap-2">
        {m.done && <CheckGlyph />}
        <h3 className={TITLE} data-action-title>{m.title}</h3>
      </div>
      {m.subtitle && <p className={`${NOTE} mt-0.5`} data-action-subtitle>{m.subtitle}</p>}
      {shown.kind === 'match' && shown.match && shown.bookingId && (
        <MatchPreview bookingId={shown.bookingId} thumbUrl={shown.match.thumbUrl} videoUrl={shown.match.videoUrl} />
      )}
      {(m.struck || m.bold) && (
        <p className={`${BODY} mt-1`}>
          {m.struck && <s className="text-db-text-2" data-action-struck>{m.struck}</s>}
          {m.struck && m.bold && ' '}
          {m.bold && <b className="font-semibold" data-action-bold>{m.bold}</b>}
        </p>
      )}
      {m.rows.length > 0 && (m.pick ? (
        <div className="mt-3 flex flex-col gap-2" role="radiogroup" aria-label="Times">
          {m.rows.map((r) => (
            <button key={r.value} type="button" role="radio" aria-checked={picked === r.value} data-action-row={r.value}
              onClick={() => setPicked(r.value)} className={`${ROW_CHOICE} ${picked === r.value ? ROW_ON : ROW_OFF}`}>
              {r.label}
            </button>
          ))}
        </div>
      ) : (
        <ul className="mt-1">
          {m.rows.map((r) => (
            <li key={r.value} className={`${BODY} font-semibold`} data-action-row={r.value}>{r.label}</li>
          ))}
        </ul>
      ))}
      {m.note && <p className={`${NOTE} mt-2`} data-action-note>{m.note}</p>}
      {m.primary && (
        <button type="button" className={`${BTN_PRIMARY} mt-4 w-full`} disabled={m.primary.disabled || working}
          aria-busy={working || undefined} onClick={() => press(m.primary!.act)} data-action-primary={m.primary.act}>
          {working ? <ButtonSpinner /> : m.primary.label}
        </button>
      )}
      {m.secondary && (
        <div className={`flex ${m.primary ? 'mt-3 justify-center' : 'mt-2'}`}>
          <button type="button" className={`${TEXT_ACTION} text-[15px] leading-[20px]`}
            onClick={() => press(m.secondary!.act)} data-action-secondary={m.secondary.act}>
            {m.secondary.label}
          </button>
        </div>
      )}
    </section>
  );
}

/** Row 7's body: the match's first frame and its length (the walk of 4 Nov, BOOKING 4). */
function MatchPreview({ bookingId, thumbUrl, videoUrl }: { bookingId: string; thumbUrl?: string; videoUrl?: string }) {
  const url = useMatchVideoUrl(videoUrl ? { id: bookingId, playerVideoUrl: videoUrl } : null);
  const [seconds, setSeconds] = useState<number | null>(null);
  return (
    <div className="mt-2 flex items-center gap-3" data-action-match>
      <div className="relative h-[54px] w-24 flex-shrink-0 overflow-hidden rounded-[8px] bg-black">
        {thumbUrl ? <img src={thumbUrl} alt="" aria-hidden="true" className="h-full w-full object-cover" data-action-match-frame /> : null}
        {/* The video's metadata gives the length; with no frame of its own, its first frame shows. */}
        {url && (
          <video src={`${url}#t=0.1`} muted playsInline preload="metadata" aria-hidden="true" tabIndex={-1}
            className={thumbUrl ? 'hidden' : 'pointer-events-none h-full w-full object-cover'} data-action-match-frame={thumbUrl ? undefined : ''}
            onLoadedMetadata={(e) => { const d = e.currentTarget.duration; if (Number.isFinite(d) && d > 0) setSeconds(d); }} />
        )}
      </div>
      {seconds !== null && <span className={`${NOTE} tabular-nums`} data-action-match-length>{matchLength(seconds)}</span>}
    </div>
  );
}

function CheckGlyph() {
  return (
    <svg width="20" height="22" viewBox="0 0 20 22" fill="none" aria-hidden="true" className="shrink-0 text-biscayne" data-action-check>
      <path d="M4.5 11.5l3.5 3.5 7.5-8" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** A card still loading draws as its own shape: the title line, two rows, the primary. */
export function ActionCardSkeleton() {
  return (
    <SkeletonGroup label="Loading">
      <div className={PANE_CARD} data-action-skeleton>
        <Skeleton width="60%" height={17} />
        <Skeleton width="40%" height={15} className="mt-2" />
        <Skeleton width="100%" height={48} radius={100} className="mt-4" />
      </div>
    </SkeletonGroup>
  );
}

/**
 * Several open cards (165 as amended): newest first, at most three, then "2 more" as a text action that opens the
 * rest. A card answered here keeps its result until the page is left. Renders nothing for none.
 */
export function ActionCards({ changes, me, tz, onAnswer, className = '', cardClassName = '' }: {
  changes: LiveChange[];
  me: 'coach' | 'player';
  tz: string;
  onAnswer: (a: ActionAnswer, change: LiveChange) => Promise<unknown> | void;
  className?: string;
  /** On a white column (desktop Lessons' list), the card takes the page grey so it doesn't vanish. */
  cardClassName?: string;
}) {
  const [all, setAll] = useState(false);
  // Answered here: the result shows until the page is left, after the change itself has gone (165: once).
  const [flipped, setFlipped] = useState<Record<string, LiveChange>>({});
  // A result that came already flipped (the mover's own "You moved Lesson 3") shows on one visit, then is gone:
  // the ids this device has shown, read once as the page opens (a per-viewer convenience).
  const [seen] = useState(readSeen);
  const doneIds = changes.filter((c) => actionCardModel(c, me, tz).done).map((c) => c.id).join(',');
  useEffect(() => { if (doneIds) writeSeen(doneIds.split(',')); }, [doneIds]);
  useActionCardStore();
  // Seen here before: a result, or an upload or count already opened (the walk of 4 Nov, BOOKING 5).
  const fresh = changes.filter((c) => !(seen.has(c.id) && (actionCardModel(c, me, tz).done || c.kind === 'match' || c.kind === 'counted'))
    && !((c.kind === 'match' || c.kind === 'counted') && flipOf(c.id)?.status === 'cleared'));
  const ids = new Set(fresh.map((c) => c.id));
  const list = [...fresh, ...Object.values(flipped).filter((c) => !ids.has(c.id))];
  if (list.length === 0) return null;
  const { shown, more } = stackCards(list, all ? Infinity : 3);
  const keep = (id: string) => (next: LiveChange | null) => setFlipped((f) => {
    const { [id]: _gone, ...rest } = f;
    return next ? { ...rest, [id]: next } : rest;
  });
  return (
    <div className={`flex flex-col gap-3 ${className}`} data-action-cards>
      {shown.map((c) => <ActionCard key={c.id} change={c} me={me} tz={tz} onAnswer={onAnswer} onFlip={keep(c.id)} className={cardClassName} />)}
      {more > 0 && (
        <button type="button" onClick={() => setAll(true)} className={`${TEXT_ACTION} self-start text-[15px] leading-[20px]`} data-action-more>
          {more} more
        </button>
      )}
    </div>
  );
}
